import { Column, Row } from "@silevis/reactgrid";
import { useEffect, useState } from "react";
import { renameField } from "../../General/Helpers";
import { useLoadData } from "../../services/DataService";
import useDataverseService from "../../services/DataverseService";
import "../ButtonComponent/ButtonComponent.css";
import { useData } from "../Contexts/DataProvider";
import { useHistoryPeriod } from "../Contexts/HistoryPeriodProvider";
import { useSelectedEntity } from "../Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Contexts/SelectedTabProvider";
import Dropdown, { Option } from "../Dropdown/Dropdown";
import Loading from "../Loading/Loading";
import BaseTable from "./BaseTable";
import "./HistoryTables.css";
import {
  columnWidth,
  DIGITS_PREFIX,
  DIGITS_WITH_UNDERSCORE,
  historyBudgetPriorityColumns,
  isDigitsOnly,
  renameHeaderRow,
} from "./TableHelpers";

export interface PeriodResponse {
  value: { new_period: string }[];
}

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    historyBudgetPriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = renameField(String(a.columnId));
        const bId = renameField(String(b.columnId));

        const aMatch = aId.match(DIGITS_WITH_UNDERSCORE);
        const bMatch = bId.match(DIGITS_WITH_UNDERSCORE);
        if (aMatch && bMatch) {
          const [, aPre, aSuf] = aMatch;
          const [, bPre, bSuf] = bMatch;
          if (aPre !== bPre) return aPre.localeCompare(bPre);
          if (aSuf && bSuf) return Number(aSuf) - Number(bSuf);
        }
        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const buildColumns = (data: any[], visibleGroups?: string[]): Column[] => {
  const uniqueColumns = new Set<string>();
  data.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "new_historybudgetid" &&
        key !== "@odata.etag" &&
        key !== "_ownerid_value"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  historyBudgetPriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });

  uniqueColumns.forEach((key) => {
    const match = renameField(key).match(DIGITS_PREFIX);
    if (
      !visibleGroups ||
      visibleGroups.length === 0 ||
      (match && visibleGroups.includes(match[1]))
    ) {
      columnsArray.push({ columnId: key, width: 90, resizable: true });
    }
  });

  return sortColumns(columnsArray);
};

const buildRows = (
  data: any[],
  columns: Column[],
  selectedPeriod?: string
): Row[] => {
  const rowHeight = 35;
  const headerRow: any = renameHeaderRow(columns);
  const filteredData = selectedPeriod
    ? data.filter((row) => row.new_period === selectedPeriod)
    : data;
  const dataRows = filteredData.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly = true;
      if (typeof value === "number") {
        return { type: "number", value, groupId: col.columnId, readOnly };
      } else if (typeof value === "string") {
        return { type: "text", text: value, groupId: col.columnId, readOnly };
      } else {
        return { type: "text", text: "", groupId: col.columnId, readOnly };
      }
    }),
    id: row.cr864_budgetrecordid,
  }));
  return [headerRow, ...dataRows];
};

interface BudgetTableProps {
  submitted?: boolean;
  visibleGroups?: string[];
}

const HistoryBudgetTable = ({ submitted = false }: BudgetTableProps) => {
  const { data } = useData();
  const { selectedEntity } = useSelectedEntity();
  const { selectedTab } = useSelectedTab();
  const { historyPeriod, setHistoryPeriod } = useHistoryPeriod();
  const { fetchDistinctValues } = useDataverseService();
  const { loadData } = useLoadData();

  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [periods, setPeriods] = useState<Option[]>([]);
  const [isPeriodsOpen, setIsPeriodsOpen] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchDistinctValues(
      "new_historybudgets",
      "new_period",
      undefined,
      selectedEntity?.new_codecompany,
      selectedTab
    ).then((r: PeriodResponse) =>
      setPeriods(
        r.value.map((obj) => ({
          label: obj.new_period,
          value: obj.new_period,
        }))
      )
    );
  }, [selectedEntity]);

  useEffect(() => {
    if (historyPeriod) {
      setLoading(true);
      loadData().finally(() => setLoading(false));
    }
  }, [historyPeriod]);

  useEffect(() => {
    if (data.value && data.value.length > 0) {
      const cols = buildColumns(data.value);
      setColumns(cols);
      setRows(buildRows(data.value, cols, selectedPeriod));
    } else {
      setColumns([]);
      setRows([]);
    }
  }, [data, submitted, selectedPeriod]);

  const handlePeriodSelect = (period: Option | Option[]) => {
    if (Array.isArray(period)) {
      setSelectedPeriod(period[0].value);
      setHistoryPeriod(period[0].value);
    } else {
      if (period.value === selectedPeriod) {
        setSelectedPeriod(undefined);
        setHistoryPeriod(undefined);
      } else {
        setSelectedPeriod(period.value);
        setHistoryPeriod(period.value);
      }
      setIsPeriodsOpen(false);
    }
  };

  return (
    <div className="historyTable">
      <Dropdown
        filterOptions={periods}
        onSelect={handlePeriodSelect}
        setOpen={setIsPeriodsOpen}
        open={isPeriodsOpen}
        multiselect={false}
        placeholder="Select period"
        className="buttonComponent"
      />
      {historyPeriod &&
        (loading ? (
          <Loading />
        ) : (
          <BaseTable
            columns={columns}
            setColumns={setColumns}
            rows={rows}
            stickyLeftColumns={8}
            onCellsChanged={() => {}}
            modifyCellValues={() => {}}
            historyTable
          />
        ))}
    </div>
  );
};

export default HistoryBudgetTable;
